import { createSlice, createAsyncThunk, PayloadAction, Draft } from '@reduxjs/toolkit';
import {
  clearScriptAnswerV2,
  fetchScriptProgressV2,
  updateScriptAnswerV2,
} from 'services/api/script.api.service';
import { setNotification } from 'state/app.state';
import {
  LoadingStates,
  ThunkAPI,
  ScriptProgressV2,
  ScriptStepV2,
  ScriptLeadStatusActions,
  LeadStatus,
  UnqualifyReason,
  UnqualifyReasonGroups,
  AppCopyText,
} from 'typings';
import { apiErrorNotification } from 'utils/functions';
import { apiInvalidPhoneLead } from './lead.state';

interface ScriptState {
  entityV2: ScriptProgressV2 | null;
  currentStep: ScriptStepV2 | null;
  currentStepName: string | null;
  loadingState: LoadingStates;
  updatingStepId: string | null;
  shouldScrollToCurrentStep: boolean;
  shouldCopyCurrentStepMessage: boolean;
  isOnScript: boolean;
  hasQualifyAnswer: boolean;
  // the lead status derived from script response
  scriptResponseLeadStatus: string;
  // the unqualified reason derived from script response
  scriptResponseUnqualifiedReason: string;
  scriptResponseSummaryString: string | null;
}

interface ScriptProgressResponseV2 {
  entity: ScriptProgressV2;
  currentStep: ScriptStepV2 | null;
}

interface ClearScriptPayloadV2 {
  scriptStepId: string;
}

interface UpdateScriptPayloadV2 {
  scriptStepId: string;
  answer: string;
}

/**
 * Asynchronous Actions (thunks)
 */
export const apiFetchScriptProgressV2 = createAsyncThunk(
  'script/apiFetchScriptProgressV2',
  async (leadId: string, thunkApi: ThunkAPI) => {
    try {
      const entity = await fetchScriptProgressV2(leadId);
      const currentStep = findCurrentStepV2(entity);

      return {
        entity,
        currentStep,
      };
    } catch (error) {
      thunkApi.dispatch(setNotification(apiErrorNotification(error)));
      return thunkApi.rejectWithValue({ error: 'Fail' });
    }
  }
);

export const apiClearScriptAnswerV2 = createAsyncThunk(
  'script/apiClearScriptAnswerV2',
  async (payload: ClearScriptPayloadV2, thunkApi: ThunkAPI) => {
    try {
      const leadId = thunkApi.getState().lead.entity?.id;
      const updatedEntity = await clearScriptAnswerV2(leadId, payload.scriptStepId);

      const currentStep = findCurrentStepV2(updatedEntity);
      if (currentStep) {
        await thunkApi.dispatch(handleClearedCurrentStepV2(currentStep));
      }

      return {
        entity: updatedEntity,
        currentStep,
      };
    } catch (error) {
      thunkApi.dispatch(setNotification(apiErrorNotification(error)));
      return thunkApi.rejectWithValue({ error: 'Fail' });
    }
  }
);

export const handleClearedCurrentStepV2 = createAsyncThunk(
  'script/handleClearedCurrentStepV2',
  async (currentStep: ScriptStepV2, thunkApi: ThunkAPI) => {
    // we've cleared the answer to a closing step, set the lead status back to "new lead"
    if (currentStep?.updateAction) {
      thunkApi.dispatch(setScriptResponseLeadStatus(LeadStatus.NEW_LEAD));
    }
  }
);

export const apiUpdateScriptAnswerV2 = createAsyncThunk(
  'script/apiUpdateScriptAnswerV2',
  async (payload: UpdateScriptPayloadV2, thunkApi: ThunkAPI) => {
    try {
      const leadId = thunkApi.getState().lead.entity?.id;
      const updatedEntity = await updateScriptAnswerV2(
        leadId,
        payload.scriptStepId,
        payload.answer
      );

      const currentStep = findCurrentStepV2(updatedEntity);
      if (currentStep) {
        await thunkApi.dispatch(handleUpdatedCurrentStepV2(currentStep));
      }

      return {
        entity: updatedEntity,
        currentStep,
      };
    } catch (error) {
      thunkApi.dispatch(setNotification(apiErrorNotification(error)));
      return thunkApi.rejectWithValue({ error: 'Fail' });
    }
  }
);

export const handleUpdatedCurrentStepV2 = createAsyncThunk(
  'script/handleUpdatedCurrentStepV2',
  async (currentStep: ScriptStepV2, thunkApi: ThunkAPI) => {
    // if we've landed on a GE/BP step, automatically take the action
    if (hasCurrentStepUpdateActionV2(currentStep, ScriptLeadStatusActions.GOOD_EMAIL_BAD_PHONE)) {
      thunkApi.dispatch(apiInvalidPhoneLead({ reason: AppCopyText.BAD_PHONE_REASON }));
      return;
    }

    // if we landed on a bad lead tech support step, set the lead as invalidated
    if (hasCurrentStepUpdateActionV2(currentStep, ScriptLeadStatusActions.BAD_LEAD_TECH_SUPPORT)) {
      thunkApi.dispatch(setScriptResponseLeadStatus(LeadStatus.BAD_LEAD_TECH_SUPPORT));
      return;
    }

    // if we've landed on a qualify step, set the lead status to qualified
    if (hasCurrentStepUpdateActionV2(currentStep, ScriptLeadStatusActions.QUALIFY)) {
      thunkApi.dispatch(setScriptResponseLeadStatus(LeadStatus.QUALIFIED));
      return;
    }

    // if we've landed on an unqualify step, set the lead status to unqualified and set the unqualify reason
    if (hasCurrentStepUpdateActionV2(currentStep, ScriptLeadStatusActions.UNQUALIFY)) {
      thunkApi.dispatch(setScriptResponseLeadStatus(LeadStatus.UNQUALIFIED));

      // auto-set unqualify reason
      let reason = null;
      const unqualifyReasons = thunkApi.getState().lead.unqualifyReasons ?? [];
      const currentStepReasonId = currentStep.updateOptions?.unqualifyReasonId;

      // if there is a specific reason coded into this step, choose it
      if (currentStepReasonId) {
        reason = unqualifyReasons.find((r: UnqualifyReason) => r.id === currentStepReasonId);
      }

      // otherwise, choose a default
      if (!reason) {
        reason = unqualifyReasons.find(
          (r: UnqualifyReason) => r.unqualifyReasonGroupId === UnqualifyReasonGroups.NOT_INTERESTED
        );
      }

      if (reason) {
        thunkApi.dispatch(setScriptResponseUnqualifiedReason(reason.name));
      }
      return;
    }
  }
);

const buildSummaryString = (script: ScriptProgressV2) => {
  let summaryStr = '';
  const summaryList = script.summary?.list;

  if (summaryList) {
    let questionAnswerList: string[] = [];

    for (const item of summaryList) {
      if (item.question && item.answer) {
        questionAnswerList.push(`${item.question}: ${item.answer}`);
      }
    }

    if (questionAnswerList) {
      summaryStr = questionAnswerList.join('\n');
    }
  }

  return summaryStr;
};

const findCurrentStepV2 = (scriptProgress: ScriptProgressV2) => {
  return (
    scriptProgress.scriptSteps?.find(
      (step: ScriptStepV2) => step.name === scriptProgress.currentStepName
    ) ?? null
  );
};

const hasCurrentStepUpdateActionV2 = (
  currentStep: ScriptStepV2 | null,
  action: ScriptLeadStatusActions
) => {
  return currentStep?.updateAction === action && currentStep?.answer === action;
};

const handleScriptProgressResponseV2 = (
  state: Draft<ScriptState>,
  action: PayloadAction<ScriptProgressResponseV2>
) => {
  state.entityV2 = {
    ...(state.entityV2 ?? {}),
    ...action.payload.entity,
  };
  state.currentStep = action.payload.currentStep;
  state.currentStepName = action.payload.entity.currentStepName;
  state.scriptResponseSummaryString = buildSummaryString(action.payload.entity);
  state.hasQualifyAnswer = hasCurrentStepUpdateActionV2(
    action.payload.currentStep,
    ScriptLeadStatusActions.QUALIFY
  );
};

/* Script State Slice */
const scriptInitialState: ScriptState = {
  entityV2: null,
  currentStep: null,
  currentStepName: null,
  loadingState: LoadingStates.NOT_LOADED,
  updatingStepId: null,
  shouldScrollToCurrentStep: false,
  shouldCopyCurrentStepMessage: false,
  isOnScript: true,
  hasQualifyAnswer: false,
  scriptResponseLeadStatus: '',
  scriptResponseUnqualifiedReason: '',
  scriptResponseSummaryString: null,
};

export const scriptSlice = createSlice({
  name: 'script',
  initialState: scriptInitialState,
  reducers: {
    // this action explicitly sets the boolean for whether a qualify answer has been chosen
    setHasQualifyAnswer: (state, action: PayloadAction<boolean>) => {
      state.hasQualifyAnswer = action.payload;
    },
    // this action sets the lead's status based on a script response answer
    setScriptResponseLeadStatus: (state, action: PayloadAction<string>) => {
      state.scriptResponseLeadStatus = action.payload;
    },
    // this action sets the lead's unqualified reason based on a script response answer
    setScriptResponseUnqualifiedReason: (state, action: PayloadAction<string>) => {
      state.scriptResponseUnqualifiedReason = action.payload;
    },
    setIsOnScript: (state, action: PayloadAction<boolean>) => {
      state.isOnScript = action.payload;
    },
    clearCopyCurrentStepMessage: (state) => {
      state.shouldCopyCurrentStepMessage = false;
    },
    clearScrollToCurrentStep: (state) => {
      state.shouldScrollToCurrentStep = false;
    },
  },
  extraReducers: {
    [apiFetchScriptProgressV2.pending.toString()]: (state) => {
      state.loadingState = LoadingStates.LOADING;
    },
    [apiFetchScriptProgressV2.rejected.toString()]: (state) => {
      state.loadingState = LoadingStates.NOT_LOADED;
    },
    [apiFetchScriptProgressV2.fulfilled.toString()]: (state, action) => {
      handleScriptProgressResponseV2(state, action);

      state.shouldScrollToCurrentStep = true;
      state.loadingState = LoadingStates.LOADED;
    },
    [apiClearScriptAnswerV2.pending.toString()]: (state, action) => {
      state.loadingState = LoadingStates.LOADING;
      state.updatingStepId = action.meta.arg.scriptStepId;
    },
    [apiClearScriptAnswerV2.rejected.toString()]: (state) => {
      state.loadingState = LoadingStates.NOT_LOADED;
      state.updatingStepId = null;
    },
    [apiClearScriptAnswerV2.fulfilled.toString()]: (state, action) => {
      handleScriptProgressResponseV2(state, action);

      state.shouldScrollToCurrentStep = true;
      state.loadingState = LoadingStates.LOADED;
      state.updatingStepId = null;
    },
    [apiUpdateScriptAnswerV2.pending.toString()]: (state, action) => {
      state.loadingState = LoadingStates.LOADING;
      state.updatingStepId = action.meta.arg.scriptStepId;
    },
    [apiUpdateScriptAnswerV2.rejected.toString()]: (state) => {
      state.loadingState = LoadingStates.NOT_LOADED;
      state.updatingStepId = null;
    },
    [apiUpdateScriptAnswerV2.fulfilled.toString()]: (state, action) => {
      handleScriptProgressResponseV2(state, action);

      state.shouldScrollToCurrentStep = true;
      state.shouldCopyCurrentStepMessage = true;
      state.loadingState = LoadingStates.LOADED;
      state.updatingStepId = null;
    },
  },
});

export const {
  setHasQualifyAnswer,
  setScriptResponseLeadStatus,
  setScriptResponseUnqualifiedReason,
  setIsOnScript,
  clearCopyCurrentStepMessage,
  clearScrollToCurrentStep,
} = scriptSlice.actions;

export default scriptSlice.reducer;
